import { useState, useEffect, ChangeEventHandler, FocusEvent } from 'react';
import { Rifm } from 'rifm';
import { default as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';

import {
    validatePhone,
    validateName,
    validateRegNumber,
    validateAccountNumber,
    validateAge,
    validateInt,
    validateFloat,
} from '../../shared/validators/validate';
import { formatPhone } from '../../shared/formatters/format-phone';
import { formatRegno } from '../../shared/formatters/format-regno';
import { formatAccount } from '../../shared/formatters/format-account';
import { formatName } from '../../shared/formatters/format-name';
import { formatAge } from '../../shared/formatters/format-age';
import { formatInt } from '../../shared/formatters/format-int';
import { formatFloat } from '../../shared/formatters/format-float';
import { FieldEventFunction, TRifmArgs, ValidatorFunction, FormatFunction } from '../../shared/types/field-types';
import { validateEmail } from '../../shared/validators/validate-email';

const DIGITS = /\d+/g;
const FLOATS = /[\d,]+/g;

interface IFieldProps {
    _multiline: boolean;
    _handleOnChange: ChangeEventHandler<HTMLInputElement>;
    _value: string;
}

type TextFieldProps = {
    /**
     * Function that will recieve the data from the field
     */
    fieldEvent?: FieldEventFunction;
    /**
     * The id of the field. Is used to associate the label with the input, and for trackin (so give it a sensible name)
     */
    id: string;
    label?: string; // The Label of the field
    required?: boolean; // If true, the input element will be required.
    disabled?: boolean; // If true, the input element will be disabled.
    errorTextInvalid?: string; // Text shown to the user if the value is invalid
    errorTextEmpty?: string; // Text shown to the user if the value is empty
    isSubmitted?: boolean; // is the form(-part) submitted
    validator?: ValidatorFunction; // Custom validator function to validate the input
    format?: FormatFunction; // Custom formatter function to format the input
    value?: string;
    fixed?: number; // fixed number of characters, i.e. bank account: 10 characters prefixed with 0
    prefix?: string; // what to prefix with
    autoComplete?: string; // Will allow the users browser to quickly fill in the users info
    type?: 'default' | 'phone' | 'regnumber' | 'accountnumber' | 'name' | 'email' | 'age' | 'int' | 'float'; // Will format and validate the value according to the selected type
    multiline?: boolean; // Make the field render as a textarea
    maxFractionDigits?: number;
} & MuiTextFieldProps;

function getFieldSettings(
    type: string,
    customValidator?: ValidatorFunction,
    customFormat?: FormatFunction,
    maxFractionDigits?: number
) {
    let validator = customValidator || (() => true);
    let accept = /.*/g;
    let format = customFormat || ((str: string) => str);
    const mask = false;

    if (type === 'phone') {
        validator = customValidator || validatePhone;
        accept = DIGITS;
        format = formatPhone;
    }

    if (type === 'regnumber') {
        validator = customValidator || validateRegNumber;
        accept = DIGITS;
        format = formatRegno;
    }

    if (type === 'accountnumber') {
        validator = customValidator || validateAccountNumber;
        accept = DIGITS;
        format = formatAccount;
    }

    if (type === 'email') {
        validator = customValidator || validateEmail;
    }

    if (type === 'name') {
        validator = customValidator || validateName;
        format = formatName;
    }

    if (type === 'age') {
        validator = customValidator || validateAge;
        format = customFormat || formatAge;
        accept = DIGITS;
    }

    if (type === 'int') {
        validator = customValidator || validateInt;
        format = formatInt;
        accept = DIGITS;
    }

    if (type === 'float') {
        validator = customValidator || validateFloat;
        format = formatFloat(maxFractionDigits);
        accept = FLOATS;
    }

    return { validator, accept, format, mask } as TRifmArgs;
}

const TextField = ({
    fieldEvent,
    id = `text-field`,
    label = 'Field label',
    required = false,
    disabled = false,
    errorTextInvalid = 'Der er fejl i det indtastede',
    errorTextEmpty = 'Feltet er obligatorisk og skal udfyldes.',
    isSubmitted = false,
    validator,
    format,
    value = '',
    fixed = 0,
    prefix = '0',
    autoComplete = 'off',
    type = 'default',
    multiline = false,
    maxFractionDigits = 0,
    ...rest
}: TextFieldProps) => {
    const fieldSettings = getFieldSettings(type, validator, format, maxFractionDigits);

    const [fieldValue, setFieldValue] = useState(value);
    const [errorText, setErrorText] = useState(errorTextEmpty);
    const [valid, setValid] = useState(true);
    const [hasFocus, setHasFocus] = useState(false);

    const removeWhiteSpace = (input: string) => input.replace(/\s+/g, '');

    useEffect(() => {
        if (value !== fieldValue) {
            setFieldValue(value);
        }
    }, [value]);

    useEffect(() => {
        setErrorText(!valid ? errorTextInvalid : errorTextEmpty);
    }, [errorTextInvalid, errorTextEmpty, valid]);

    const handleOnChange = (formattedValue: string) => {
        const inputValue = type === 'phone' ? removeWhiteSpace(formattedValue) : formattedValue;
        setFieldValue(inputValue);
        if (inputValue === '') {
            const status = isSubmitted ? 'invalid' : 'valid';
            fieldEvent && fieldEvent({ value: inputValue, status: status, event: 'change' });
            if (required && isSubmitted) {
                setValid(false);
            }
        } else {
            const status = !fieldSettings.validator(inputValue) && required ? 'invalid' : 'valid';
            fieldEvent && fieldEvent({ value: inputValue, status: status, event: 'change' });
            if (required) {
                setValid(status === 'valid');
            }
        }
    };

    function handleOnBlur(event: FocusEvent<HTMLInputElement>) {
        let inputValue = event.target.value;
        if (inputValue === '') {
            // dirty means neither valid nor invalid. Can only happen when field is empty
            const status = isSubmitted && required ? 'invalid' : required ? 'dirty' : 'valid';
            fieldEvent && fieldEvent({ value: inputValue, status: status, event: 'blur' });
            if (required && isSubmitted) {
                setValid(false);
            }
            setErrorText(errorTextEmpty);
        } else {
            const status = !fieldSettings.validator(inputValue) ? 'invalid' : 'valid';
            if (status === 'valid' && fixed && prefix) {
                inputValue = inputValue.padStart(fixed, prefix);
                setFieldValue(inputValue);
            }
            fieldEvent &&
                fieldEvent({
                    value: type === 'phone' ? removeWhiteSpace(inputValue) : inputValue,
                    status: status,
                    event: 'blur',
                });
            setValid(status === 'valid');
            setErrorText(errorTextInvalid);
        }
        setHasFocus(false);
    }

    const handleOnFocus = () => {
        setHasFocus(true);
    };

    const field = ({ _multiline, _handleOnChange, _value }: IFieldProps): JSX.Element => {
        return (
            <MuiTextField
                error={(!valid || _value === '') && isSubmitted && !hasFocus}
                required={required}
                disabled={disabled}
                id={`inner-${id}`}
                label={label}
                type={fieldSettings.accept === DIGITS ? 'tel' : 'text'}
                helperText={(!valid || _value === '') && isSubmitted && !hasFocus ? errorText : undefined}
                fullWidth
                margin="normal"
                onChange={_handleOnChange}
                onBlur={handleOnBlur}
                onFocus={handleOnFocus}
                value={_value}
                multiline={_multiline}
                {...rest}
            />
        );
    };

    return multiline ? (
        field({
            _multiline: multiline,
            _handleOnChange: (event) => handleOnChange(event.target.value),
            _value: fieldValue,
        })
    ) : (
        <Rifm {...fieldSettings} value={fieldValue} onChange={handleOnChange}>
            {({ value, onChange }) => field({ _multiline: false, _handleOnChange: onChange, _value: value })}
        </Rifm>
    );
};

export { TextField };
export type { TextFieldProps };
